import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  EntitySliceState,
  createEntitySlice
} from "@/services/api/entitystore";
import { mountStoreDevtool } from "simple-zustand-devtools";
import { CompanyData } from "@/pages/companies/types";
import { PartnerData } from "@/pages/partners/types";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { UserData } from "@/entities/users/types";
import { LeadData } from "@/pages/leads/types";
import { PersonData } from "@/entities/people/types";
import { DealData } from "@/pages/deals/types";
import { CustomFieldsStore } from "@/services/api/customfields/types";
import { createCustomFieldsSlice } from "@/services/api/customfields/store";
import { ActivityData } from "@/entities/activities/types";
import { SettingsState } from "@/entities/settings/types";
import { createSettingsSlice } from "@/entities/settings/store";
import { TaskData } from "@/entities/tasks/types";
import {
  License,
  Product,
  SubIndustry
} from "@/pages/settings/components/settingsItems/hooks/types";
import { LogData } from "@/entities/history/types";
import { LookupSliceState, createLookupSlice } from "./lookupSlice";

export const MIN_CHARACTERS_FOR_SEARCH = 3;
export const MAX_ENTITIES_PER_PAGE_API_LIMIT = 25;
export const MAX_ENTITIES_PER_PAGE = MAX_ENTITIES_PER_PAGE_API_LIMIT;

interface BoundStoreState {
  companies: EntitySliceState<CompanyData>;
  partners: EntitySliceState<PartnerData>;
  partners_contacts: EntitySliceState<PartnerContactData>;
  users: EntitySliceState<UserData>;
  leads: EntitySliceState<LeadData>;
  deals: EntitySliceState<DealData>;
  people: EntitySliceState<PersonData>;
  customFields: CustomFieldsStore;
  activities: EntitySliceState<ActivityData>;
  settings: SettingsState;
  tasks: EntitySliceState<TaskData>;
  products: EntitySliceState<Product>;
  products_licenses: EntitySliceState<License>;
  history: EntitySliceState<LogData>;
  lookup: LookupSliceState;
  subIndustries: EntitySliceState<SubIndustry>;
}

const useBoundStore = create<BoundStoreState>()(
  immer((...a) => ({
    companies: createEntitySlice<CompanyData, BoundStoreState>()("companies")(
      ...a
    ),
    partners: createEntitySlice<PartnerData, BoundStoreState>()("partners")(
      ...a
    ),
    partners_contacts: createEntitySlice<PartnerContactData, BoundStoreState>()(
      "partners_contacts"
    )(...a),
    users: createEntitySlice<UserData, BoundStoreState>()("users")(...a),
    leads: createEntitySlice<LeadData, BoundStoreState>()("leads")(...a),
    deals: createEntitySlice<DealData, BoundStoreState>()("deals")(...a),
    people: createEntitySlice<PersonData, BoundStoreState>()("people")(...a),
    customFields: createCustomFieldsSlice(...a),
    activities: createEntitySlice<ActivityData, BoundStoreState>()(
      "activities"
    )(...a),
    settings: createSettingsSlice(...a).settings,
    tasks: createEntitySlice<TaskData, BoundStoreState>()("tasks")(...a),
    products: createEntitySlice<Product, BoundStoreState>()("products")(...a),
    products_licenses: createEntitySlice<License, BoundStoreState>()(
      "products_licenses"
    )(...a),
    history: createEntitySlice<LogData, BoundStoreState>()("history")(...a),
    lookup: createLookupSlice(...a),
    subIndustries: createEntitySlice<SubIndustry, BoundStoreState>()(
      "subIndustries"
    )(...a)
  }))
);

if (import.meta.env.DEV) {
  mountStoreDevtool("BoundStore", useBoundStore);
}

export default useBoundStore;
